import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useQuery, useConvex } from 'convex/react';
import { api } from '../convex/_generated/api';
import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import { identifyUser, resetAnalytics } from '../services/analytics';
import * as Sentry from '@sentry/react';

const AuthContext = createContext<any>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Profile fields we care about for "isProfileComplete"
const REQUIRED_PROFILE_FIELDS = [
  'phoneNumber',
  'streetAddress',
  'city',
  'state',
  'zipCode',
];

const FRIENDLY_FIELD_LABELS = {
  phoneNumber: 'Phone number',
  streetAddress: 'Street address',
  city: 'City',
  state: 'State',
  zipCode: 'ZIP code',
};

// Helper: treat null, undefined, "", and "   " as missing
const isMissing = (value: unknown) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  return false;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { isSignedIn, isLoaded } = useClerkAuth();
  const { user: clerkUser } = useUser();
  const convex = useConvex();

  // --- 1. Sync Clerk user → Convex safely ---
  useEffect(() => {
    if (!isLoaded || !clerkUser) return;

    let cancelled = false;

    const startSync = async () => {
      // tiny delay so Convex identity is ready
      await new Promise((res) => setTimeout(res, 600));

      try {
        await convex.mutation(api.users.syncUser, {
          email: clerkUser.primaryEmailAddress?.emailAddress ?? '',
          name:
            clerkUser.fullName ?? clerkUser.username ?? 'Unknown Adventurer',
          imageUrl: clerkUser.imageUrl ?? null,
        });

        if (!cancelled) {
          // User synced to Convex successfully
        }
      } catch (err) {
        console.error('Convex sync error:', err);
        Sentry.captureException(err);
      }
    };

    startSync();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, clerkUser, convex]);

  // --- 2. Load Convex user + admin status ---
  const convexUser = useQuery(
    api.users.getCurrentUserFromAuth,
    isSignedIn ? {} : 'skip'
  );

  const adminStatus = useQuery(
    api.users.isAdminFromAuth,
    isSignedIn ? {} : 'skip'
  );

  // --- 2.5. Identify user for analytics (FIR-85) ---
  useEffect(() => {
    if (convexUser?._id) {
      identifyUser(convexUser._id, {
        email: convexUser.email,
        name: convexUser.name,
        isAdmin: !!adminStatus,
      });
    } else if (!isSignedIn && isLoaded) {
      // Reset analytics identity on logout
      resetAnalytics();
    }
  }, [
    convexUser?._id,
    convexUser?.email,
    convexUser?.name,
    adminStatus,
    isSignedIn,
    isLoaded,
  ]);

  // --- 3. Compute profile completeness from Convex user ---
  const { isProfileComplete, missingProfileFields, friendlyFieldLabels } =
    useMemo(() => {
      // While loading (undefined), treat as not complete but with no missing list yet
      if (convexUser === undefined) {
        return {
          isProfileComplete: false,
          missingProfileFields: [],
          friendlyFieldLabels: FRIENDLY_FIELD_LABELS,
        };
      }

      // If we somehow have no record, everything is missing
      if (!convexUser) {
        return {
          isProfileComplete: false,
          missingProfileFields: [...REQUIRED_PROFILE_FIELDS],
          friendlyFieldLabels: FRIENDLY_FIELD_LABELS,
        };
      }

      const missing = REQUIRED_PROFILE_FIELDS.filter((field) =>
        isMissing(convexUser[field])
      );

      return {
        isProfileComplete: missing.length === 0,
        missingProfileFields: missing,
        friendlyFieldLabels: FRIENDLY_FIELD_LABELS,
      };
    }, [convexUser]);

  // --- 4. Final context value ---
  const value = {
    // prefer Convex user (has profile fields), fall back to Clerk
    user: convexUser || clerkUser || null,
    convexUser: convexUser || null,
    clerkUser: clerkUser || null,

    isAdmin: !!adminStatus,
    isAdminLoading: adminStatus === undefined && !!isSignedIn,
    isAuthenticated: !!isSignedIn,

    // profile completeness API used by UploadGame, Messages, ProfileCompletionCallout
    isProfileComplete,
    missingProfileFields,
    friendlyFieldLabels,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
